import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import { cn, formatIDR } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { Repeat2, Sparkles, Users2 } from 'lucide-react'
import React, { useEffect } from 'react'

interface CustomerInsightsWidgetProps {
  className?: string
}

export const CustomerInsightsWidget: React.FC<CustomerInsightsWidgetProps> = ({ className }) => {
  const { toast } = useToast()

  const { data, isLoading, error } = useQuery({
    queryKey: ['customerAnalytics', 365],
    queryFn: () => apiClient.getCustomerAnalytics({ period: 365 }),
  })

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to load customer insights',
        variant: 'destructive',
      })
    }
  }, [error, toast])

  const analytics = data?.data
  const segmentEntries = analytics ? Object.entries(analytics.customer_segments) : []

  const summaryItems = analytics
    ? [
        {
          label: 'Active Customers',
          value: analytics.active_customers,
          tone: 'border-blue-200 bg-blue-50 text-blue-700',
        },
        {
          label: 'Returning Customers',
          value: analytics.returning_customers,
          tone: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        },
        {
          label: 'Retention Rate',
          value: `${analytics.customer_retention_rate.toFixed(1)}%`,
          tone: 'border-violet-200 bg-violet-50 text-violet-700',
        },
        {
          label: 'Customer LTV',
          value: formatIDR(analytics.customer_lifetime_value),
          tone: 'border-amber-200 bg-amber-50 text-amber-700',
        },
      ]
    : []

  return (
    <Card className={cn('rounded-3xl border-slate-200/80 bg-white shadow-sm', className)}>
      <CardHeader className="border-b border-slate-100 pb-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-lg font-semibold text-slate-950">
              Customer Insights
            </CardTitle>
            <CardDescription className="mt-1 text-sm text-slate-500">
              Customer quality, retention, and segment health over the last 12 months.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="rounded-2xl border border-sky-200 bg-sky-50 p-2.5 text-sky-700">
              <Users2 className="h-5 w-5" />
            </div>
            <div className="rounded-2xl border border-violet-200 bg-violet-50 p-2.5 text-violet-700">
              <Sparkles className="h-5 w-5" />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-4">
        {isLoading ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-24 rounded-2xl" />
              ))}
            </div>
            <Skeleton className="h-36 rounded-2xl" />
          </>
        ) : analytics ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              {summaryItems.map((item) => (
                <div key={item.label} className={cn('rounded-2xl border px-4 py-3', item.tone)}>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] opacity-80">
                    {item.label}
                  </div>
                  <div className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Repeat2 className="h-4 w-4 text-slate-500" />
                Customer Segments
              </div>
              <div className="mt-4 space-y-3">
                {segmentEntries.map(([segment, count]) => {
                  const total = analytics.total_customers || 1
                  const percentage = (count / total) * 100
                  return (
                    <div key={segment}>
                      <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
                        <span className="font-medium capitalize">{segment.replace('_', ' ')}</span>
                        <span>{count}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                        <div
                          className="h-full rounded-full bg-blue-600"
                          style={{ width: `${Math.max(percentage, count > 0 ? 6 : 0)}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}
